import { motion } from 'framer-motion';
import { CategoryValue } from 'shared/components/charts/types';
import { GROW, STAGGER, TRANSITION } from 'shared/components/motion/tokens';
import { useRevealOnce } from 'shared/components/motion/useRevealOnce';
import { categoricalColor } from 'shared/constants/chartPalette';

interface ProductMixBarProps {
  items: CategoryValue[];
  formatValue?: (value: number) => string;
  /** Sobrepõe a paleta categórica — use para dados de status (ex: pago/pendente/atrasado),
   * onde a cor já tem um significado reservado (ver STATUS_COLORS) em vez de identificar
   * uma categoria arbitrária. */
  colors?: string[];
}

const defaultFormatValue = (value: number) => `R$ ${value.toLocaleString('pt-BR')}`;

export function ProductMixBar({ items, formatValue = defaultFormatValue, colors }: ProductMixBarProps) {
  const [ref, inView] = useRevealOnce();
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const colored = items.map((item, index) => ({
    ...item,
    color: colors?.[index] ?? categoricalColor(index),
    percentage: (item.value / total) * 100,
  }));

  return (
    <div ref={ref}>
      <div className="flex h-8 w-full overflow-hidden rounded-lg bg-chip">
        {colored.map((item, index) => (
          <motion.div
            key={item.id}
            style={{ backgroundColor: item.color }}
            className="border-r-2 border-surface transition-[filter] last:border-r-0 hover:brightness-110"
            title={`${item.label}: ${formatValue(item.value)} (${item.percentage.toFixed(0)}%)`}
            initial={{ width: 0 }}
            animate={{ width: inView ? `${item.percentage}%` : 0 }}
            transition={{ ...GROW, delay: inView ? index * STAGGER : 0 }}
          />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
        {colored.map((item, index) => (
          <motion.div
            key={item.id}
            className="flex items-center gap-2"
            initial={{ opacity: 0, y: 4 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ ...TRANSITION, delay: 0.3 + index * STAGGER }}
          >
            <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-ink-secondary">{item.label}</span>
            <span className="font-medium text-ink">
              {formatValue(item.value)} · {item.percentage.toFixed(0)}%
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
